import { T, clone, cond, has, map, omit, applySpec, prop, propOr } from 'ramda';
import { ConfigComponent, ConfigSection } from "../_types";
import { isNotUndefined } from "ramda-adjunct";
import { ScreenProps } from "../../components-common/_types";
import { toTitleCase } from '@dirootie-toolbox/strings';
import { isScreenProps } from './validators';

export const isSection = has("screens");

export const isConfigComponent = (elem: any) => !isSection(elem) && !has("tmp", elem);

export const setSection = (template: any, section?: ConfigSection) =>
  ({...template, section: isNotUndefined(section) ? (mapSection(clone({...omit(["screens"],section!)}))) : undefined});

export const createConfigInfoElement = (type: "template"|"screen") => (element: ConfigComponent) => ({type, element})

export const createFromComp = createConfigInfoElement("template");
export const createTemplateScreenElement = createConfigInfoElement("screen");

export const createFromComponent = (item: ConfigComponent, section: ConfigSection) => [createFromComp(setSection(item,section))];
export const createFromScreen = (item: ScreenProps) => [createConfigInfoElement("screen")(item)];
export const createFromSection = (item: ConfigSection) => map((x) => createFromComponent(x,item), item.screens!)


export const mapSection = (section: Omit<ConfigSection,"screens">) => applySpec<Omit<ConfigSection,"screens">>({
  id: prop("id"),
  sectionId: prop("sectionId"),
  sectionIdRaw: prop("sectionIdRaw"),
  path: prop("path"),
  sectionPath: prop("sectionPath"),
  sectionPathRaw: prop("sectionPathRaw"),
  title: (o:any) => toTitleCase(propOr(prop("id",o),"title")(o)),
  roles: propOr([], "roles"),
  protected: (obj:any) => prop("protected",obj) === true ? true : false,
  breadcrumbs: propOr(true, "breadcrumbs"),
  props: propOr({}, "props"),
  layout: propOr(undefined, "layout"),
})(section);



// Function to determine the transformation based on the object properties
export const sanitizeWithSession = cond([
  [isSection, createFromSection],
  [isScreenProps, createFromScreen],
  [isConfigComponent, createFromComponent as any],
  [T, createFromScreen as any]
]);

export const flatAndMergeAndSetSections = (sectionParam: ConfigSection): ConfigComponent[] => {
  const section = clone(sectionParam);
  const mergeSections = (child: ConfigSection,parent?: ConfigSection): ConfigSection => {
    const currentId = child.id ? child.id.replace(/-/g, '_') : ((child.path ? child.path.replace(/\//g, '_') : "errors/not-found-id"));
    const currentPath = child.path ? child.path : ((child.id ? child.id.replace(/_/g, '-') : "errors/not-found-path"));
    if (!parent) {

      return {
        ...child,
        protected: prop("protected",child) === true ? true : false,
        sectionPathRaw: [currentPath],
        sectionPath: currentPath,
        sectionIdRaw: [currentId],
        sectionId: currentId,
      };
    }
    const { sectionIdRaw, sectionPathRaw} = parent;
    const currentFinalIdRaw = [...(sectionIdRaw || []), currentId];
    const currentFinalId = currentFinalIdRaw.join('_');
    const currentSectionPathRaw = [...(sectionPathRaw || []), currentPath];
    const currentSectionPath = currentSectionPathRaw.join('/');
    return {
      ...parent,
      ...child,
      sectionPathRaw: currentSectionPathRaw,
      sectionPath: currentSectionPath,
      sectionIdRaw: currentFinalIdRaw,
      sectionId: currentFinalId,
      id: currentId,
      path: currentPath
    };
  };

  const traverse = (section: ConfigSection, parent?: ConfigSection): ConfigComponent[] => {
    const mergedSection = mergeSections(section, parent);
    let screensWithSections: ConfigComponent[] = [];
    if(section.screens) {
    screensWithSections = section.screens!.map(screen => ({
      ...screen,
      tmp: has("tmp",screen) ? prop("tmp",screen) : section.tmp?? "common",
      section: {
        ...mergedSection,
        sections: undefined,
        screens: undefined
      }
    })) as any;
  }
    const childSectionsScreens = (section.sections || []).flatMap(childSection =>
      traverse(childSection,mergedSection)
    );

    return [...screensWithSections, ...childSectionsScreens];
  };

  return traverse(section);
};
